/**
 * Instant (in-memory) period visit counts for Opening / Closing pool cards.
 *
 * The dashboard already has `jobs` on first paint; the authoritative bundle
 * fetch (~0.5s) applies sent/paid exclusion + per-customer window. Without
 * that, the instant count over-counts tagged jobs already on sent invoices
 * (the "4 → 1" / "3 → 0" flicker).
 *
 * After each successful bundle fetch we seed a lightweight session cache
 * (billed job ids + window_start per customer). Subsequent renders — including
 * right after logging a new visit — use the cache to produce an instant count
 * that matches the bundle rules, without waiting on the network again.
 */

/** @type {Map<string, { billedJobIds: Set<string>, windowStart: string|null }>} */
const cache = new Map();

function cacheKey(tenantId, context, customerId) {
  return `${tenantId}:${context}:${String(customerId)}`;
}

/**
 * @param {{
 *   tenantId: string,
 *   context: 'opening' | 'closing',
 *   customerIds: string[],
 *   windowStartByCustomer: Map<string, string>,
 *   billedByCustomer: Map<string, Set<string>>,
 * }} params
 */
export function seedPoolPeriodInstantCache({
  tenantId,
  context,
  customerIds = [],
  windowStartByCustomer = new Map(),
  billedByCustomer = new Map(),
}) {
  if (!tenantId || !context) return;
  for (const id of customerIds) {
    const cid = String(id);
    const key = cacheKey(tenantId, context, cid);
    const existing = cache.get(key);
    const incomingBilled = billedByCustomer.get(cid);
    // Merge non-destructively. TWO paths write here: the authoritative bundle
    // fetch (full year-unbounded billed set + real window) and a best-effort
    // fast path (billed from the dashboard's year-scoped invoices, null window).
    // Union the billed ids and keep any non-null window, so a partial/null
    // fast-path seed can never DOWNGRADE a richer authoritative entry. The old
    // REPLACE let the fast path overwrite a real window with null + a partial
    // billed set → under-exclusion → the instant count over-counted billed jobs
    // until the bundle reconciled (the "5 visits / 0 rows" regression).
    const mergedBilled = new Set(existing ? existing.billedJobIds : []);
    if (incomingBilled) for (const jid of incomingBilled) mergedBilled.add(jid);
    const incomingWindow = windowStartByCustomer.get(cid) ?? null;
    cache.set(key, {
      billedJobIds: mergedBilled,
      windowStart: incomingWindow ?? existing?.windowStart ?? null,
    });
  }
}

/** Test-only reset. */
export function clearPoolPeriodInstantCache() {
  cache.clear();
}

/**
 * Drop one customer's cached billed/window entry (BOTH contexts). Call when its
 * lifecycle status changes: that moves `window_start`, and the cached entry still
 * holds the OLD window — so `countPeriodJobsInMemory` would let pre-window jobs
 * through until the fresh bundle lands (the re-open "4 → 0" over-count flicker).
 * customerId is a UUID (globally unique), so a suffix match is tenant-safe.
 */
export function invalidatePoolPeriodInstantCacheForCustomer(customerId) {
  if (customerId == null) return;
  const suffix = `:${String(customerId)}`;
  for (const key of cache.keys()) {
    if (key.endsWith(suffix)) cache.delete(key);
  }
}

/**
 * Count in-context jobs for one customer using the same sent/paid + window
 * rules as the bundle assembler (when cache entries exist).
 *
 * @param {{
 *   jobs: Array,
 *   customerId: string,
 *   context: string,
 *   transitionDate: string,
 *   billedJobIds?: Set<string>,
 *   windowStart?: string|null,
 * }} params
 * @returns {number}
 */
export function countPeriodJobsInMemory({
  jobs = [],
  customerId,
  context,
  transitionDate,
  billedJobIds = new Set(),
  windowStart = null,
}) {
  // NOTE (2026-09-18, field-read-routing fix): a null windowStart here is
  // NOT ambiguous the way it used to be at the bundle layer — this fn is
  // also the engine behind the FAST-PATH pre-bundle estimate (seeded from
  // the dashboard's own invoices, deliberately with NO per-customer window
  // yet — see seedPoolPeriodInstantCache's docblock), which intentionally
  // has no lower bound so a genuine visit is visible before the
  // authoritative bundle lands. Narrowing this branch would break that path
  // (usePrefetchPoolPeriodBundles.test.js pins it). The bundle-side risk
  // (an ALREADY-LOADED bundle with a genuinely-null windowStart claiming a
  // count its own zero rows can't back up) is guarded at the CALL SITE
  // instead — see PoolCustomerQuickList's `inMemPeriodCountFor`.
  const cid = String(customerId);
  return (jobs || []).filter((j) => {
    if (!j?.id || String(j.customer_id) !== cid) return false;
    if (j.job_context !== context) return false;
    if (billedJobIds.has(String(j.id))) return false;
    if (windowStart && j.date && j.date < windowStart) return false;
    if (transitionDate && j.date && j.date > transitionDate) return false;
    return true;
  }).length;
}

/**
 * Instant period count for a pool card — billed-aware ONLY.
 *
 * Returns a count exclusively when a billed-exclusion source exists (the session
 * cache, seeded by a bundle fetch / the fast-path). When there is NO billed-aware
 * data yet it returns 0 — it must NOT tag-only count, because that includes jobs
 * already on sent/paid invoices and produces the "5 visits → none" over-count
 * flicker once the authoritative bundle lands. Under-counting for the sub-second
 * before the (persisted) bundle fills in is the safe direction; over-counting is
 * the bug we are eliminating.
 *
 * @param {{
 *   tenantId: string|null,
 *   context: 'opening' | 'closing',
 *   customerId: string,
 *   jobs: Array,
 *   transitionDate: string,
 * }} params
 * @returns {number}
 */
export function getInstantPeriodJobCount({
  tenantId,
  context,
  customerId,
  jobs = [],
  transitionDate,
}) {
  if (!tenantId || !context || !customerId) return 0;
  const entry = cache.get(cacheKey(tenantId, context, customerId));
  if (!entry) return 0;
  return countPeriodJobsInMemory({
    jobs,
    customerId,
    context,
    transitionDate,
    billedJobIds: entry.billedJobIds,
    windowStart: entry.windowStart,
  });
}
